import { useMemo } from 'react'
import { useAuth } from '@/core/AuthContext'
import { SEOHead } from '../../components/SEOHead'
import { ExerciseLibraryView } from '../../components/trainer/ExerciseLibraryView'
import { StatPill } from '../../components/trainer/StatPill'
import { Button } from '../../components/ui'
import { useTrainerExercises } from '../../lib/useTrainerExercises'
import { useIsMobile } from '../../lib/useIsMobile'

export function Exercises() {
  const { userData, signOut, user } = useAuth()
  const trainerId = useMemo(
    () => userData?.trainerProfileId || userData?.trainerId || user?.uid,
    [userData, user]
  )
  const isMobile = useIsMobile()
  const {
    addExerciseMedia,
    addExternalMedia,
    draftHasFormData,
    error,
    exerciseForm,
    exerciseMedia,
    exerciseSearch,
    filteredExercises,
    hasDraft,
    isUploading,
    removeExercise,
    removeExerciseMedia,
    removeUpload,
    resetExerciseDraft,
    retryUpload,
    saveExercise,
    selectedExerciseId,
    setExerciseForm,
    setExerciseSearch,
    startExerciseEdit,
    uploads,
    exercises,
  } = useTrainerExercises(trainerId)

  if (!trainerId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Trainer not linked</p>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Your account is missing a trainerId claim. Sign out and re-open your
            secret link, or create the trainer via Cloud Function.
          </p>
          <Button variant="secondary" onClick={signOut} className="mt-4">
            Sign out
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <SEOHead
        title="Exercise Library | Start Grounded"
        description="Create and manage your trainer exercise library"
        canonicalUrl="https://startgrounded.app/trainer/exercises"
      />
      <div className="p-6" data-testid="trainer-exercise-library">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
                Exercise Management
              </p>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                Exercise Library
              </h1>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Build and maintain your exercise catalog for workout templates.
              </p>
            </div>
          </div>

          {!isMobile && (
            <div className="grid gap-3 sm:grid-cols-3">
              <StatPill label="Exercises" value={exercises.length} />
              <StatPill label="Results" value={filteredExercises.length} />
              <StatPill label="Drafts" value={hasDraft ? 1 : 0} />
            </div>
          )}

          <ExerciseLibraryView
            filteredExercises={filteredExercises}
            exerciseSearch={exerciseSearch}
            exerciseForm={exerciseForm}
            exerciseMedia={exerciseMedia}
            uploads={uploads}
            isUploading={isUploading}
            selectedExerciseId={selectedExerciseId}
            error={error}
            hasDraft={hasDraft}
            draftHasFormData={draftHasFormData}
            draftMediaCount={exerciseMedia.length}
            onSearchChange={setExerciseSearch}
            onExerciseFormChange={setExerciseForm}
            onEditExercise={startExerciseEdit}
            onDeleteExercise={(id) => void removeExercise(id)}
            onSaveExercise={() => void saveExercise()}
            onResetDraft={(clearDraft) => void resetExerciseDraft(clearDraft)}
            onAddMedia={(files) => void addExerciseMedia(files)}
            onAddExternalMedia={(url) => void addExternalMedia(url)}
            onRemoveMedia={(mediaId) => removeExerciseMedia(mediaId)}
            onRetryUpload={retryUpload}
            onRemoveUpload={removeUpload}
          />
        </div>
      </div>
    </>
  )
}
